/**
 * Unified Parent Portal Sidebar - Parent Portal Refactor
 * 
 * CREATED FOR: Parent Portal refactor & redesign (Phase 1.2)
 * PURPOSE: Unify navigation structures - merge mobile sectioned approach with desktop
 * TECH DEBT: Eliminates separate Mobile/Desktop sidebar components
 * 
 * This component addresses critical tech debt by:
 * - Merging the rich sectioned mobile navigation with desktop functionality
 * - Creating a single responsive navigation component
 * - Implementing unified badge/notification system across all platforms
 * - Using the new responsive design tokens for consistent styling
 * 
 * Key improvements:
 * - Uses sectioned navigation structure from mobile (better organization)
 * - Maintains badge system with real-time notifications
 * - Responsive design with mobile-first approach
 * - Consistent animations and interactions across devices
 * - Unified styling using new design tokens
 */

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { NavLink } from "react-router-dom";
import { 
  LayoutDashboard,
  GraduationCap,
  CalendarDays,
  Camera,
  PlusCircle,
  ClipboardList,
  MessageSquare,
  Bell,
  Phone,
  CreditCard,
  FileText,
  Settings,
  User,
  X,
  LogOut,
  Home,
  ShoppingBag,
  ChevronLeft,
  Video
} from "lucide-react";
import { ScrollArea } from "../ui/scroll-area";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { useResponsive } from "@/hooks/useResponsive";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import { useAuth } from "@/hooks/useAuth";
import { useParent } from "@/hooks/useParent";
import { 
  RESPONSIVE_SPACING, 
  RESPONSIVE_TYPOGRAPHY, 
  RESPONSIVE_ANIMATIONS,
  TOUCH_TARGETS 
} from "@/design-tokens/responsive";

interface ParentSidebarUnifiedProps {
  isOpen?: boolean;
  onToggle?: () => void;
  onClose?: () => void;
}

interface MenuSection {
  title: string;
  items: MenuItem[];
}

interface MenuItem {
  title: string;
  href: string;
  icon: React.ReactNode;
  badge?: {
    count: number;
    color: 'red' | 'green' | 'blue' | 'orange';
  };
}

export default function ParentSidebarUnified({ 
  isOpen = true, 
  onToggle, 
  onClose 
}: ParentSidebarUnifiedProps) {
  const { isMobile, isTablet, isDesktop } = useResponsive();
  const [isHovered, setIsHovered] = useState(false);
  const { user, signOut } = useAuth();
  const { parent } = useParent();
  const { unreadCount } = useUnreadMessages();

  // Keyboard support for closing sidebar
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && (isMobile || isTablet)) {
        onClose?.();
      }
    };

    if (isOpen && (isMobile || isTablet)) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, isMobile, isTablet, onClose]);

  // Unified menu structure - using the rich sectioned approach from mobile
  const menuSections: MenuSection[] = [
    {
      title: "Overview",
      items: [
        {
          title: "Dashboard",
          href: "/parent-portal",
          icon: <Home className="w-5 h-5" />
        }
      ]
    },
    {
      title: "My Children",
      items: [
        {
          title: "Student Profiles",
          href: "/parent-portal/students",
          icon: <User className="w-5 h-5" />
        },
        {
          title: "Class Schedules",
          href: "/parent-portal/classes",
          icon: <CalendarDays className="w-5 h-5" />
        },
        {
          title: "HiveShare",
          href: "/parent-portal/videos",
          icon: <Video className="w-5 h-5" />
        }
      ]
    },
    {
      title: "Enrollments",
      items: [
        {
          title: "Current Classes",
          href: "/parent-portal/classes?view=current",
          icon: <GraduationCap className="w-5 h-5" />
        },
        {
          title: "Browse Activities",
          href: "/parent-portal/available-courses",
          icon: <PlusCircle className="w-5 h-5" />
        },
        {
          title: "Waitlists",
          href: "/parent-portal/waitlists",
          icon: <ClipboardList className="w-5 h-5" />
        }
      ]
    },
    {
      title: "Communication",
      items: [
        {
          title: "Messages",
          href: "/parent-portal/messages",
          icon: <MessageSquare className="w-5 h-5" />,
          badge: unreadCount > 0 ? {
            count: unreadCount,
            color: 'red'
          } : undefined
        },
        {
          title: "Notifications",
          href: "/parent-portal/notifications",
          icon: <Bell className="w-5 h-5" />
        },
        {
          title: "Emergency Contacts",
          href: "/parent-portal/emergency-contacts",
          icon: <Phone className="w-5 h-5" />
        }
      ]
    },
    {
      title: "Account",
      items: [
        {
          title: "Billing & Payments",
          href: "/parent-portal/billing",
          icon: <CreditCard className="w-5 h-5" />
        },
        {
          title: "Forms & Documents",
          href: "/parent-portal/documents",
          icon: <FileText className="w-5 h-5" />
        },
        {
          title: "Apparel Shop",
          href: "/parent-portal/apparel",
          icon: <ShoppingBag className="w-5 h-5" />
        },
        {
          title: "Settings",
          href: "/parent-portal/profile",
          icon: <Settings className="w-5 h-5" />
        }
      ]
    }
  ];

  const getBadgeColor = (color: string) => {
    const colors = {
      red: 'bg-red-500',
      green: 'bg-green-500',
      blue: 'bg-blue-600',
      orange: 'bg-orange-500'
    };
    return colors[color as keyof typeof colors] || 'bg-gray-500';
  };

  const renderHeader = () => (
    <div 
      className={cn(
        "flex items-center justify-center border-b border-gray-200/60 bg-white/80 backdrop-blur-sm",
        RESPONSIVE_SPACING.container.mobile,
        RESPONSIVE_SPACING.container.tablet,
        "py-6 md:py-8"
      )}
    >
      <img 
        src="https://imagedelivery.net/ylmXutM6_iJT3CT9AqBdjQ/15521506-bf83-4c94-881f-2ed325767800/public" 
        alt="Hivetivity Logo" 
        className={cn(
          "object-contain",
          isMobile ? "h-12 w-auto" : "h-16 w-auto"
        )}
        onError={(e) => {
          // Fallback to text if image fails to load
          const target = e.target as HTMLImageElement;
          target.style.display = 'none';
          target.parentElement!.innerHTML = '<span class="text-gray-600 font-bold text-2xl">H</span>';
        }}
      />
      
      {isMobile && (
        <div className="absolute top-4 right-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onClose || onToggle}
            style={{ minHeight: TOUCH_TARGETS.minSize, minWidth: TOUCH_TARGETS.minSize }}
            className="rounded-full hover:bg-gray-100 transition"
          >
            <X className="w-5 h-5 text-gray-400" />
            <span className="sr-only">Close Sidebar</span>
          </Button>
        </div>
      )}
    </div>
  );

  const renderMenuItem = (item: MenuItem, isActive: boolean) => (
    <NavLink
        to={item.href}
        end={item.href === "/parent-portal"}
        className={cn(
          "flex items-center gap-3 rounded-xl transition font-medium group relative text-left",
          RESPONSIVE_SPACING.list.padding,
          RESPONSIVE_TYPOGRAPHY.navItem,
          // Touch target optimization
          "min-h-[44px]",
          isActive
            ? "bg-purple-100 text-purple-900 font-semibold"
            : "hover:bg-gray-50 text-gray-700",
          // Mobile-specific tap states
          isMobile ? RESPONSIVE_ANIMATIONS.listItem.mobile : RESPONSIVE_ANIMATIONS.listItem.desktop
        )}
        onClick={() => isMobile && (onClose || onToggle)?.()}
        style={{ minHeight: TOUCH_TARGETS.minSize }}
      >
        <div
          className={cn(
            "transition",
            isActive 
              ? "text-purple-700" 
              : "text-gray-500 group-hover:text-purple-600"
          )}
        >
          {item.icon}
        </div>
        
        <span className="flex-1">{item.title}</span>
        
        {item.badge && (
          <Badge 
            className={cn(
              "text-white text-xs font-semibold rounded-full flex items-center justify-center p-0",
              isMobile ? "w-4 h-4" : "w-5 h-5",
              getBadgeColor(item.badge.color)
            )}
          >
            {item.badge.count}
          </Badge>
        )}
      </NavLink>
  );

  const renderSection = (section: MenuSection, sectionIndex: number) => (
    <div 
      key={section.title}
      className={cn(RESPONSIVE_SPACING.section.mobile, "mb-3 last:mb-0")}
    >
      <div className={cn(
        "uppercase text-xs text-purple-600 font-semibold mb-3 text-left tracking-wide",
        RESPONSIVE_SPACING.container.mobile
      )}>
        {section.title}
      </div>
      <div className="space-y-1">
        {section.items.map((item) => (
          <NavLink key={item.href} to={item.href} end={item.href === "/parent-portal"}>
            {({ isActive }) => renderMenuItem(item, isActive)}
          </NavLink>
        ))}
      </div>
    </div>
  );

  const renderFooter = () => (
    <div 
      className={cn(
        "flex items-center gap-3 border-t border-gray-200/60 bg-white/80 backdrop-blur-sm",
        RESPONSIVE_SPACING.container.mobile,
        RESPONSIVE_SPACING.container.tablet,
        "py-4 md:py-5"
      )}
    >
      <div 
        className={cn(
          "rounded-xl bg-purple-500 flex items-center justify-center text-white font-bold shadow-sm",
          isMobile ? "w-8 h-8 text-sm" : "w-10 h-10 text-base"
        )}
      >
        {parent?.first_name?.charAt(0)?.toUpperCase() || ''}
        {parent?.last_name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'P'}
      </div>
      
      <div className="flex-1 min-w-0">
        <div className={cn(
          "font-semibold text-gray-900 leading-tight truncate",
          isMobile ? "text-sm" : "text-base"
        )}>
          {parent?.first_name && parent?.last_name ? `${parent.first_name} ${parent.last_name}` : user?.user_metadata?.full_name || 'Parent User'}
        </div>
        <div className="text-xs text-purple-600 font-medium">
          Parent Account
        </div>
      </div>
      
      <div>
        <Button
          onClick={signOut}
          variant={isMobile ? "ghost" : "outline"}
          size="sm"
          className={cn(
            "flex items-center gap-2 rounded-xl transition",
            isMobile 
              ? "px-2 py-1 text-red-600 hover:bg-red-50 text-xs" 
              : "px-3 py-2 border-red-200 bg-red-50 hover:bg-red-100 shadow-sm"
          )}
          style={{ minHeight: TOUCH_TARGETS.minSize }}
        >
          <LogOut className={cn(isMobile ? "w-3 h-3" : "w-4 h-4", "text-red-500")} />
          <span className={cn(
            "text-red-600 font-semibold",
            isMobile ? "text-xs" : "text-sm"
          )}>
            Logout
          </span>
        </Button>
      </div>
    </div>
  );

  // Responsive sidebar styling
  const sidebarClasses = cn(
    "h-screen bg-white flex flex-col overflow-hidden relative",
    // Mobile: Full screen overlay
    isMobile && [
      "fixed inset-0 z-50 shadow-2xl",
      "w-screen"
    ],
    // Tablet: Slide-out panel
    isTablet && [
      "fixed left-0 top-0 z-40 shadow-2xl border-r border-purple-200/50",
      "w-72"
    ],
    // Desktop: Fixed sidebar
    isDesktop && [
      "w-72 lg:w-80 shadow-lg border-r border-gray-200/60",
      "bg-white/95 backdrop-blur-sm"
    ]
  );

  const sidebarContent = (
    <motion.div
      className={sidebarClasses}
      onMouseEnter={() => !isMobile && setIsHovered(true)}
      onMouseLeave={() => !isMobile && setIsHovered(false)}
      initial={isMobile || isTablet ? { x: -100, opacity: 0 } : {}}
      animate={isMobile || isTablet ? { x: 0, opacity: 1 } : {}}
      exit={isMobile || isTablet ? { x: -100, opacity: 0 } : {}}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      {/* Background gradient */}
      <motion.div
        className="absolute inset-0 opacity-30"
        animate={{ 
          background: isHovered && !isMobile
            ? "radial-gradient(circle at 50% 50%, rgba(168, 85, 247, 0.1) 0%, transparent 50%)"
            : "radial-gradient(circle at 50% 50%, rgba(168, 85, 247, 0.05) 0%, transparent 50%)"
        }}
        transition={{ duration: 0.5 }}
      />

      <div className="relative z-10 flex flex-col h-full">
        {renderHeader()}
        
        <ScrollArea className="flex-1 overflow-hidden">
          <nav className={cn(
            "pt-4 pb-2",
            RESPONSIVE_SPACING.container.mobile,
            RESPONSIVE_SPACING.container.tablet
          )}>
            {menuSections.map((section, index) => renderSection(section, index))}
          </nav>
        </ScrollArea>
        
        {renderFooter()}
      </div>
    </motion.div>
  );

  // Mobile: Render with backdrop
  if (isMobile || isTablet) {
    return (
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose || onToggle}
            />
            {sidebarContent}
          </>
        )}
      </AnimatePresence>
    );
  }

  // Desktop: Always visible
  return sidebarContent;
}